import { useCallback, useEffect, useState } from 'react';

interface UseDelayedCommitValueParams<T> {
  commitDelayMs?: number;
  onCommit?: (value: T) => void;
  value: T;
}

export const useDelayedCommitValue = <T>({
  commitDelayMs = 0,
  onCommit,
  value,
}: UseDelayedCommitValueParams<T>) => {
  const [localValue, setLocalValue] = useState(value);
  const shouldCommitDelayed = commitDelayMs > 0;
  const resolvedValue = shouldCommitDelayed ? localValue : value;

  useEffect(() => {
    if (!shouldCommitDelayed) {
      return;
    }

    setLocalValue(value);
  }, [shouldCommitDelayed, value]);

  useEffect(() => {
    if (!shouldCommitDelayed || localValue === value) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      onCommit?.(localValue);
    }, commitDelayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [commitDelayMs, localValue, onCommit, shouldCommitDelayed, value]);

  const setValue = useCallback((nextValue: T) => {
    if (shouldCommitDelayed) {
      setLocalValue(nextValue);
      return;
    }

    onCommit?.(nextValue);
  }, [onCommit, shouldCommitDelayed]);

  return {
    setValue,
    value: resolvedValue,
  };
};
